import React, { createContext, useContext, useEffect, useState } from "react";
import { useData } from "../data/DataProvider";
import { Plural, StaticTextsLanguage } from "../../types/AppData";

const DEFAULT_LANG_CODE = "en"


type StaticTextContextValue = {
    setLanguageCode: (code: string) => void
    getStaticText: (key: string, count?: number | null, lang?: string | null) => string
    languageCode: string
}


const StaticTextContext = createContext<StaticTextContextValue>({
    setLanguageCode: (code: string) => { },
    getStaticText: (key: string, count?: number | null, lang?: string | null) => "",
    languageCode: DEFAULT_LANG_CODE
})




export const StaticTextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const { data } = useData()
    const [code, setCode] = useState<string>(DEFAULT_LANG_CODE)
    const [defaultLanguageData, setDefaultLanguageData] = useState<StaticTextsLanguage>({})
    const [pluralRules, setPluralRules] = useState<Intl.PluralRules>(new Intl.PluralRules(DEFAULT_LANG_CODE))

    useEffect(() => {
        if (!data) {
            return
        }

        // Получаем язык браузера (например, "ru-RU" -> "ru")
        let browserLang = navigator.language.split('-')[0]

        // Особый случай: индонезийский язык может быть "id", но в data.static хранится как "in"
        if (browserLang === "id" && data.static["in"]) {
            browserLang = "in"
        }

        // Используем язык браузера, если он доступен в data.static, иначе DEFAULT_LANG_CODE
        const languageToUse = data.static[browserLang] ? browserLang : DEFAULT_LANG_CODE

        setDefaultLanguageData(data.static[languageToUse])
        setCode(languageToUse)
    }, [data])


    useEffect(() => {
        setPluralRules(new Intl.PluralRules(code))
    }, [code])


    function getText(key: string, count: number | null = null, lang: string | null = null): string {
        try {
            if (!data) {
                return ""
            }
            if (!lang) {
                lang = code
            }
            let langData = data!.static[lang]
            if (langData === undefined && lang === "id") {
                langData = data!.static["in"]
            }
            if (langData === undefined) {
                langData = defaultLanguageData
            }
            let item = langData[key]
            if (item === undefined) {
                item = defaultLanguageData[key]
            }
            if (typeof item !== "string" && count === null) {
                throw new Error(`Key ${key} has plural type!!!`)
            }
            if (count === null) {
                return item as string
            }
            item = item as Plural
            const pluralKey = pluralRules.select(count) as string
            let text = pluralKey in item ? item[pluralKey as keyof Plural] : item.other
            let result = text ?? item.other
            if (result.includes('%d')) {
                return result.replace('%d', `${count}`)
            }
            return result
        } catch (error) {
            console.warn(error)
            return ""
        }

    }

    return (
        <StaticTextContext.Provider value={{
            setLanguageCode: setCode,
            getStaticText: getText,
            languageCode: code
        }}>
            {children}
        </StaticTextContext.Provider>
    )
}

export const useStaticText = () => useContext(StaticTextContext);